/**
 * Horizon (Shopify) - Click-to-lock dropdown submenu
 * - Click on a top-level menu item that has a submenu: opens + locks
 * - Submenu links remain clickable (we do NOT intercept clicks inside the submenu)
 * - Stays open even if mouse leaves (Horizon closes on hover-leave; we re-open immediately)
 * - Click outside header OR press ESC: closes/unlocks
 */

(() => {
  const HEADER_SELECTOR = 'header, .header, #header, .shopify-section-header';
  const ITEM_SELECTOR = 'li.menu-list__list-item';
  const SUBMENU_SELECTOR = '.menu-list__submenu';
  const LOCK_CLASS = 'is-locked-open';

  // Evita reabrir imediatamente depois de fechar
  let suppressForceOpenUntil = 0;

  function now() {
    return Date.now();
  }

  function getMenuItemsWithSubmenu() {
    return Array.from(document.querySelectorAll(ITEM_SELECTOR)).filter((li) =>
      li.querySelector(SUBMENU_SELECTOR)
    );
  }

  function openByTheme(li) {
    try {
      li.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    } catch (_) {
      li.dispatchEvent(new Event('pointerenter'));
    }
  }

  function closeByTheme(li) {
    try {
      li.dispatchEvent(new PointerEvent('pointerleave', { bubbles: false }));
    } catch (_) {
      li.dispatchEvent(new Event('pointerleave'));
    }
  }

  function lockItem(li) {
    li.classList.add(LOCK_CLASS);
    openByTheme(li);

    // Em algumas builds do Horizon, focus ajuda a manter estado aberto
    const a = li.querySelector(':scope > a') || li.querySelector('a');
    if (a) a.focus({ preventScroll: true });
  }

  function unlockItem(li) {
    li.classList.remove(LOCK_CLASS);
    closeByTheme(li);
  }

  function unlockAll() {
    // Bloqueia reabertura por um instante (evita “luta” com o tema)
    suppressForceOpenUntil = now() + 300;
    getMenuItemsWithSubmenu().forEach(unlockItem);
  }

  function isInsideHeader(target) {
    return !!target.closest(HEADER_SELECTOR);
  }

  // Click = toggle lock (SÓ no item pai; não interfere nos links do submenu)
  document.addEventListener(
    'click',
    (e) => {
      const li = e.target.closest(ITEM_SELECTOR);
      if (!li) return;

      // Só para itens com submenu
      if (!li.querySelector(SUBMENU_SELECTOR)) return;

      // Se clicou dentro do submenu, deixa navegar normalmente
      if (e.target.closest(SUBMENU_SELECTOR)) return;

      // Toggle apenas quando o clique for no link pai do item (top-level)
      const topLink = li.querySelector(':scope > a');
      const clickedA = e.target.closest('a');
      const clickedTitle = e.target.closest('.menu-list__link-title');

      // Consideramos "toggle" se:
      // - clicou no próprio topLink, OU
      // - clicou no título (span) dentro dele
      const isTopClick =
        (clickedA && topLink && clickedA === topLink) ||
        (!!clickedTitle && topLink && topLink.contains(clickedTitle));

      if (!isTopClick) return;

      // Impede navegação do link pai (porque o objetivo é abrir/fechar o submenu)
      e.preventDefault();
      e.stopPropagation();

      const wasLocked = li.classList.contains(LOCK_CLASS);

      // Fecha todos e reabre apenas se não estava locked
      unlockAll();
      if (!wasLocked) lockItem(li);
    },
    true
  );

  // Reopen guards (per item)
  function attachReopenGuards() {
    getMenuItemsWithSubmenu().forEach((li) => {
      if (li.__menuLockAttached) return;
      li.__menuLockAttached = true;

      const reopenIfLocked = () => {
        if (!li.classList.contains(LOCK_CLASS)) return;
        if (now() < suppressForceOpenUntil) return;

        setTimeout(() => {
          if (li.classList.contains(LOCK_CLASS) && now() >= suppressForceOpenUntil) {
            openByTheme(li);
          }
        }, 0);
      };

      li.addEventListener('pointerleave', reopenIfLocked);
      li.addEventListener('mouseleave', reopenIfLocked);

      // Blur (keyboard/focus)
      li.addEventListener('blur', reopenIfLocked, true);
    });
  }

  attachReopenGuards();
  window.addEventListener('load', attachReopenGuards);
  document.addEventListener('shopify:section:load', attachReopenGuards);

  // DOM changes (sticky header / re-render)
  const observer = new MutationObserver(() => attachReopenGuards());
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Close/unlock: click outside header OR click inside header but outside locked item
  document.addEventListener(
    'click',
    (e) => {
      if (!isInsideHeader(e.target)) {
        unlockAll();
        return;
      }

      const locked = document.querySelector(`${ITEM_SELECTOR}.${LOCK_CLASS}`);
      if (locked && !e.target.closest(`${ITEM_SELECTOR}.${LOCK_CLASS}`)) {
        unlockAll();
      }
    },
    { capture: true }
  );

  // Close/unlock: ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') unlockAll();
  });

  // FORCE OPEN while locked (for global close handlers)
  function isSubmenuVisible(li) {
    const sub = li.querySelector(SUBMENU_SELECTOR);
    if (!sub) return false;
    const rect = sub.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function forceOpenWhileLocked() {
    if (now() < suppressForceOpenUntil) return;

    const locked = document.querySelector(`${ITEM_SELECTOR}.${LOCK_CLASS}`);
    if (!locked) return;

    const link = locked.querySelector(':scope > a[aria-haspopup="true"]') || locked.querySelector('a[aria-haspopup="true"]');
    if (!link) return;

    const expanded = link.getAttribute('aria-expanded') === 'true';
    const visible = isSubmenuVisible(locked);

    if (!expanded || !visible) {
      openByTheme(locked);
      link.focus({ preventScroll: true });
    }
  }

  setInterval(forceOpenWhileLocked, 80);
})();
